<template>
  <button
    class="calendar-select-button group"
    :class="{active: showCalendar}"
    @click="showCalendar = !showCalendar"
  >
    <BcIcon
      icon="calendar"
      color="white"
    />
    <span class="flex items-center gap-x-1">
      <span class="date">{{ startDate }}</span><span class="text-primary/40">-</span><span
        class="date"
      >{{ endDate }}</span><span class="font-bold">(UTC)</span>
    </span>
    <span
      v-if="showCalendar"
      class="date-picker absolute left-1/2 top-[calc(100%+0.5rem)] -translate-x-1/2"
      ref="calendarRef"
      @click.stop=""
    >
      <DatePicker
        borderless
        v-model="selectedDate"
        is-dark
        :min-date="props.minDate ?? '2009-01-01'"
        :max-date="yesterday"
        locale="en"
        is-required
        @day-click="closeCalendar"
        timezone="UTC"
      />
    </span>
  </button>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { DateTime } from 'luxon'
import { DatePicker } from 'v-calendar'
import { BcIcon } from 'xangle-ui'

import { DATE_FORMATS } from '~/utils/dateFormat'

interface IProps {
  date?: string
  minDate?: string
}

const props = defineProps<IProps>()

const emits = defineEmits([ 'changeSelectedDate' ])

const yesterday = DateTime.now().toUTC().minus({ day: 1 }).toFormat(DATE_FORMATS.dateOnlyDash)

const showCalendar = ref<boolean>(false)
const selectedDate = ref<string>(props.date ?? yesterday)
const calendarRef = ref<HTMLElement | null>(null)

const startDate = computed(() => DateTime.fromJSDate(new Date(selectedDate.value)).toUTC().minus({ day: 29 }).toFormat('MMM dd, yyyy'))
const endDate = computed(() => DateTime.fromJSDate(new Date(selectedDate.value)).toUTC().toFormat('MMM dd, yyyy'))

const closeCalendar = () => {
  showCalendar.value = false
}

watch(() => selectedDate.value, () => {
  emits('changeSelectedDate', DateTime.fromJSDate(new Date(selectedDate.value)).toUTC().toFormat('yyyy-MM-dd'))
  closeCalendar()
})

onClickOutside(calendarRef, () => {
  closeCalendar()
})
</script>

<style scoped lang="scss">
.calendar-select-button {
  @apply relative z-[2000] flex cursor-pointer justify-center items-center gap-x-2 rounded-lg bg-primary/5 p-2 hover:bg-primary/10 text-primary;

  &:focus, &.active {
    @apply bg-explorer-blue;
  }

  .date {
    @apply font-bold text-lg px-1;
  }
}
</style>